import type {
  IChartApi,
  ISeriesApi,
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  SeriesType,
  Time,
} from 'lightweight-charts'
import type { IndicatorBand } from '../indicators/types'
import { toLocalChartTime } from '../chartTime'

interface BandItem {
  x1: number
  x2: number
  yTop: number
  yBottom: number
  color: string
  opacity: number
}

class BandPaneView implements IPrimitivePaneView {
  private _source: BandPrimitive
  private _items: BandItem[] = []

  constructor(source: BandPrimitive) {
    this._source = source
  }

  update(): void {
    const chart = this._source.chart
    const series = this._source.series
    if (!chart || !series) {
      this._items = []
      return
    }
    const ts = chart.timeScale()
    const items: BandItem[] = []
    for (const band of this._source.bands) {
      const x1 = ts.timeToCoordinate(toLocalChartTime(band.fromT) as Time)
      const x2 = ts.timeToCoordinate(toLocalChartTime(band.toT) as Time)
      const yTop = series.priceToCoordinate(band.topValue)
      const yBottom = series.priceToCoordinate(band.bottomValue)
      if (x1 == null || x2 == null || yTop == null || yBottom == null) continue
      items.push({
        x1: x1 as number,
        x2: x2 as number,
        yTop: yTop as number,
        yBottom: yBottom as number,
        color: band.color,
        opacity: band.opacity,
      })
    }
    this._items = items
  }

  renderer(): IPrimitivePaneRenderer {
    const items = this._items
    return {
      draw(target) {
        target.useBitmapCoordinateSpace(
          ({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
            for (const it of items) {
              const left = Math.round(Math.min(it.x1, it.x2) * hpr)
              const right = Math.round(Math.max(it.x1, it.x2) * hpr)
              const top = Math.round(Math.min(it.yTop, it.yBottom) * vpr)
              const bottom = Math.round(Math.max(it.yTop, it.yBottom) * vpr)
              const w = right - left
              const h = bottom - top
              if (w <= 0 || h <= 0) continue

              ctx.globalAlpha = it.opacity
              ctx.fillStyle = it.color
              ctx.fillRect(left, top, w, h)

              ctx.globalAlpha = Math.min(1, it.opacity * 2.5)
              ctx.strokeStyle = it.color
              ctx.lineWidth = Math.max(1, Math.round(vpr))
              ctx.beginPath()
              ctx.moveTo(left, top)
              ctx.lineTo(right, top)
              ctx.moveTo(left, bottom)
              ctx.lineTo(right, bottom)
              ctx.stroke()
            }
            ctx.globalAlpha = 1
          },
        )
      },
    }
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'normal'
  }
}

/**
 * Series primitive that paints `IndicatorBand` rectangles directly onto the
 * chart canvas. Replaces the SVG-overlay approach — the chart drives redraws
 * automatically on pan/zoom/resize so no manual tick state is needed.
 */
export class BandPrimitive implements ISeriesPrimitive<Time> {
  chart: IChartApi | null = null
  series: ISeriesApi<SeriesType> | null = null
  bands: IndicatorBand[] = []
  private _requestUpdate: (() => void) | undefined
  private _paneView: BandPaneView

  constructor() {
    this._paneView = new BandPaneView(this)
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this.chart = param.chart
    this.series = param.series as ISeriesApi<SeriesType>
    this._requestUpdate = param.requestUpdate
  }

  detached(): void {
    this.chart = null
    this.series = null
    this._requestUpdate = undefined
  }

  setBands(bands: IndicatorBand[]): void {
    this.bands = bands
    this._paneView.update()
    this._requestUpdate?.()
  }

  updateAllViews(): void {
    this._paneView.update()
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [this._paneView]
  }
}
